import React, { useEffect, useState, memo, useCallback } from 'react';
import { getBrukerDisplayName } from '@klp/user/utils';
import { Select, MenuItem, FormControl, InputLabel } from '@material-ui/core';

import { Loader } from '@klp/design/Loader';
import { useDispatch, useSelector } from 'react-redux';

import { leverandorActions } from '../../store/leverandorSlice';
import { useLazyGetUsersByLeverandorRoleAndRegionQuery } from '../../services/rtkApi';
import type { TildelAnsvarligProps } from './TildelAnsvarligProps';
import { useRoleMapping } from '@klp/leverandor/hooks';
import { Block } from '@klp/design/Block';
import { ActionButton } from '@klp/design/Action';
import { useKlpStyles } from '@klp/design/Theme/useKlpStyles';
import { TildelKommentarField } from '../TildelKommentar/TildelKommentarField';
import { useTildelKommentar } from '../TildelKommentar/hooks/useTildelKommentar';
import { useAssignResponsibleUser } from './hooks/useAssignResponsibleUser';

const styles = () => ({
    buttonContainer: {
        display: 'flex',
        justifyContent: 'flex-end',
    },
});

const TildelAnsvarligSelectorComponent: React.FC<TildelAnsvarligProps> = ({
    superOfficeContactId,
    prosessInstansId,
    kandidatGruppe,
    currentAnsvarlig,
    redirectTo,
    activity,
}) => {
    const classes = useKlpStyles({}, styles);
    const { handleMapping: handleRoleMapping } = useRoleMapping();
    const [selectedUser, setSelectedUser] = useState<string>(
        currentAnsvarlig ?? '',
    );
    const [currentComment, setCurrentComment] = useState<string>('');

    const [
        getUsersByLeverandorRoleAndRegion,
        { data: usersData, isLoading: usersLoading },
    ] = useLazyGetUsersByLeverandorRoleAndRegionQuery();
    const dispatch = useDispatch();

    // Memoized selector to prevent unnecessary re-renders
    const regionContext = useSelector(
        useCallback((state: any) => state.prosjekt?.regionContext, []),
    );

    const handleClose = useCallback(() => {
        dispatch(leverandorActions.setActiveModal(undefined));
    }, [dispatch]);

    const handleSelectedUserChange = useCallback(
        (event: React.ChangeEvent<{ value: string }>) => {
            setSelectedUser(event.target.value);
        },
        [],
    );

    useEffect(() => {
        if (!kandidatGruppe) return;
        getUsersByLeverandorRoleAndRegion({
            rolle: kandidatGruppe,
            region: regionContext,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [kandidatGruppe, regionContext]);

    const { isTildelKommentarerFieldInTheForm, maxCommentLength } =
        useTildelKommentar(activity);
    const { handleAssignResponsibleUser, assigningTask } =
        useAssignResponsibleUser(
            superOfficeContactId,
            prosessInstansId,
            activity,
        );

    const handleCommentChange = useCallback(
        (comment: string) => {
            const remaining = maxCommentLength - comment.length;
            if (remaining < 0) return;
            setCurrentComment(comment);
        },
        [maxCommentLength],
    );

    const handleSubmit = useCallback(async () => {
        if (!selectedUser || !superOfficeContactId) return;

        handleAssignResponsibleUser(selectedUser, currentComment, redirectTo);

        handleClose();
    }, [
        selectedUser,
        superOfficeContactId,
        handleAssignResponsibleUser,
        currentComment,
        redirectTo,
        handleClose,
    ]);

    return (
        <Loader isLoading={usersLoading}>
            <Block marginBottom="compact">
                <FormControl fullWidth={true}>
                    <InputLabel id="users-label">
                        {handleRoleMapping(kandidatGruppe)}
                    </InputLabel>
                    <Select
                        labelId="users-label"
                        id="users"
                        value={selectedUser}
                        onChange={handleSelectedUserChange}
                    >
                        {usersData?.map((user) => (
                            <MenuItem
                                key={user?.brukerId}
                                value={user?.brukerId}
                            >
                                {getBrukerDisplayName(
                                    user?.fulltnavn,
                                    user?.epost,
                                    user?.brukerId,
                                )}
                            </MenuItem>
                        ))}
                    </Select>
                    {isTildelKommentarerFieldInTheForm && (
                        <Block marginTop="bulky">
                            <TildelKommentarField
                                activity={activity}
                                value={currentComment}
                                onChange={handleCommentChange}
                                maxLength={maxCommentLength}
                            />
                        </Block>
                    )}
                </FormControl>

                <Block marginTop="bulky" className={classes.buttonContainer}>
                    <ActionButton
                        isLoading={assigningTask}
                        actionType="forward"
                        onClick={handleSubmit}
                        translation="leverandor.table.tildelAnsvarlig.tildel"
                    />
                </Block>
            </Block>
        </Loader>
    );
};

export const TildelAnsvarligSelector = memo(TildelAnsvarligSelectorComponent);
